import { useCallback, useMemo } from "react";

import { useBlockEnvironment } from "@/domain/public-dashboard/components/dashboard-renderer/block-environment-context";
import { useGetPublicWorkspaceMembersForDashboard } from "@/domain/public-dashboard/hooks/api/use-get-public-workspace-members-for-dashboard";
import { TeamMembershipRole, TeamMembershipStatus } from "@/domain/team/models/team-model";
import { useUnsafeCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import {
  WorkspaceMembershipRole,
  WorkspaceMembershipStatus,
} from "@/domain/workspace/models/workspace-membership-model";

import { useGetUserListForWorkspace } from "./use-get-user-list";

/**
 * Options for useSmartUserList hook
 */
export interface UseSmartUserListOptions {
  /**
   * Workspace ID to fetch users for (used when context doesn't provide it)
   */
  workspaceId?: string;
  /**
   * Whether to enable data fetching
   * @default true
   */
  enabled?: boolean;
}

/**
 * User type compatible with both public and private contexts
 */
export interface SmartUser {
  id: string;
  email: string;
  username: string | null;
  fullname: string | null;
  profilePicture: string | null;
  workspaceMembershipList: Array<{
    id: string;
    userId: string;
    workspaceId: string;
    status: string;
    role: string;
  }>;
  teamMembershipList: Array<{
    id: string;
    userId: string;
    teamId: string;
    status: string;
    role: string;
  }>;
}

/**
 * Filter configuration for workspace memberships
 */
interface WorkspaceMembershipFilters {
  role?: WorkspaceMembershipRole[];
  status?: WorkspaceMembershipStatus[];
}

/**
 * Filter configuration for team memberships
 */
interface TeamMembershipFilters {
  teamIdList: string[];
  role?: TeamMembershipRole[];
  status?: TeamMembershipStatus[];
}

/**
 * Filter options for user list
 */
interface UserFilters {
  workspaceMembership?: WorkspaceMembershipFilters;
  teamMembership?: TeamMembershipFilters;
}

/**
 * Result type for useSmartUserList hook
 */
export interface UseSmartUserListResult {
  /**
   * List of users (sanitized for public or full for private)
   */
  userList: SmartUser[];

  /**
   * Function to filter users by workspace/team membership
   */
  filterUsers: (filters?: UserFilters) => SmartUser[];

  /**
   * Loading state
   */
  loading: boolean;

  /**
   * Error state
   */
  error: unknown;

  /**
   * Whether the hook is in public mode
   */
  isPublic: boolean;
}

/**
 * Helper function to check if workspace membership matches filters
 */
const matchesWorkspaceFilters = (user: SmartUser, filters?: WorkspaceMembershipFilters): boolean => {
  if (!filters) return true;

  const roleMatches =
    !filters.role ||
    user.workspaceMembershipList?.some((membership) =>
      filters.role?.includes(membership.role as WorkspaceMembershipRole),
    );

  const statusMatches =
    !filters.status ||
    user.workspaceMembershipList?.some((membership) =>
      filters.status?.includes(membership.status as WorkspaceMembershipStatus),
    );

  return roleMatches && statusMatches;
};

/**
 * Helper function to check if team membership matches filters
 */
const matchesTeamFilters = (user: SmartUser, filters?: TeamMembershipFilters): boolean => {
  if (!filters) return true;

  const relevantTeamMemberships =
    user.teamMembershipList?.filter((membership) => filters.teamIdList?.includes(membership.teamId)) ?? [];

  const teamIdMatches = relevantTeamMemberships.length > 0;
  const roleMatches =
    !filters.role ||
    relevantTeamMemberships.some((membership) => filters.role?.includes(membership.role as TeamMembershipRole));
  const statusMatches =
    !filters.status ||
    relevantTeamMemberships.some((membership) => filters.status?.includes(membership.status as TeamMembershipStatus));

  return teamIdMatches && roleMatches && statusMatches;
};

/**
 * Smart hook that fetches user list based on environment context.
 *
 * **Security**:
 * - Public dashboards: Uses sanitized workspace members (no email/username)
 * - Private dashboards: Uses full user list with permissions
 *
 * **Usage**:
 * ```typescript
 * const { userList, filterUsers, isPublic } = useSmartUserList();
 * ```
 *
 * @param options - Configuration options
 * @returns User list with filtering capabilities
 */
export function useSmartUserList(options?: UseSmartUserListOptions): UseSmartUserListResult {
  // Get environment context
  const context = useBlockEnvironment();
  const { environment, dashboardId, workspaceId: contextWorkspaceId } = context;

  // Try to get workspaceId from workspace context (for normal flow when BlockEnvironment doesn't have it)
  const currentWorkspace = useUnsafeCurrentWorkspaceContext();
  const workspaceContextId = currentWorkspace?.currentWorkspace?.id;

  // Determine if we're in public mode (embed = public, preview = private/edit)
  // Only consider it public if we have both embed environment AND a dashboardId
  const isPublic = environment === "embed" && !!dashboardId;

  // Use workspaceId from BlockEnvironment context (preferred), then workspace context, then options
  const effectiveWorkspaceId = contextWorkspaceId ?? workspaceContextId ?? options?.workspaceId;

  // Fetch public workspace members (only when in public mode)
  const publicMembers = useGetPublicWorkspaceMembersForDashboard(dashboardId ?? "", {
    skip: !isPublic || !dashboardId,
  });

  // Fetch private user list (only when NOT in public mode)
  const privateUsers = useGetUserListForWorkspace({
    workspaceId: effectiveWorkspaceId ?? "",
    enabled: !isPublic && !!effectiveWorkspaceId && options?.enabled !== false,
  });
  // Map public members to user format for compatibility
  const mappedPublicUsers = useMemo((): SmartUser[] => {
    if (!isPublic || !publicMembers.members) return [];

    return publicMembers.members.map(
      (member: { id: string; displayName: string; avatarUrl?: string | null; teamIdList: string[] }) => ({
        id: member.id,
        fullname: member.displayName,
        username: member.displayName, // Use displayName as username for compatibility
        email: "", // SECURITY: Never expose email in public context
        profilePicture: member.avatarUrl ?? null,
        // Create synthetic workspace membership for filtering compatibility
        workspaceMembershipList: [
          {
            id: `synthetic-${member.id}-${contextWorkspaceId}`,
            userId: member.id,
            workspaceId: contextWorkspaceId ?? "",
            status: "ACTIVE", // All public members are considered active
            role: "MEMBER", // Default role for public context
          },
        ],
        // Map team IDs to synthetic team memberships for filtering
        teamMembershipList:
          member.teamIdList?.map((teamId) => ({
            id: `synthetic-${member.id}-${teamId}`,
            userId: member.id,
            teamId: teamId,
            status: "ACTIVE", // All public team members are considered active
            role: "MEMBER", // Default role for public context
          })) ?? [],
      }),
    );
  }, [isPublic, publicMembers.members, contextWorkspaceId]);

  // Select appropriate user list based on context
  const userList = useMemo((): SmartUser[] => {
    if (isPublic) {
      return mappedPublicUsers;
    }
    // Cast private user list to SmartUser type (they're compatible)
    return (privateUsers.userList || []) as SmartUser[];
  }, [isPublic, mappedPublicUsers, privateUsers.userList]);

  // Create filter function for public context
  const filterPublicUsers = useCallback(
    (filters?: UserFilters): SmartUser[] => {
      if (!mappedPublicUsers) return [];

      // Simple filtering without caching for public context
      return mappedPublicUsers.filter((user) => {
        const workspaceMatches = matchesWorkspaceFilters(user, filters?.workspaceMembership);
        const teamMatches = matchesTeamFilters(user, filters?.teamMembership);
        return workspaceMatches && teamMatches;
      });
    },
    [mappedPublicUsers],
  );

  // Select appropriate filter function
  const filterUsers: (filters?: UserFilters) => SmartUser[] = isPublic
    ? filterPublicUsers
    : (filters?: UserFilters) => privateUsers.filterUsers(filters) as SmartUser[];

  // Determine loading state
  const loading = isPublic ? publicMembers.loading : privateUsers.loading;

  // Determine error state
  const error = isPublic ? publicMembers.error : privateUsers.error;

  return {
    userList,
    filterUsers,
    loading,
    error,
    isPublic,
  };
}
